import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, makeStyles, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import ErrorIcon from '@material-ui/icons/Error';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { AtCircularProgress } from '../../../components/AtCircularProgress';

const alertConfig = {
    downtime: {
        bg: '#F8FAFC',
        border: '#DC2626',
        iconColor: '#DC2626',
        Icon: CancelIcon,
    },
    partial: {
        bg: '#F8FAFC',
        border: '#F59E0B',
        iconColor: '#F59E0B',
        Icon: ErrorIcon,
    }
};

const useStyles = makeStyles((theme) => ({
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        marginBottom: theme.spacing(3),
    },
    title: {
        fontWeight: 700,
        fontSize: '1.25rem',
        color: '#111827',
    },
    scrollList: {
        flex: 1, 
        overflowY: 'auto', 
        minHeight: 0,
        maxHeight: 350,
        display: 'flex',
        flexDirection: 'column',
        paddingRight: theme.spacing(1),
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.divider,
            borderRadius: '10px',
        },
    },
    alertItem: {
        display: 'flex',
        gap: theme.spacing(2),
        padding: '16px 20px',
        borderRadius: '12px',
        marginBottom: theme.spacing(1.5),
        borderLeft: '5px solid',
        alignItems: 'flex-start',
        cursor: 'pointer',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'transform 0.1s ease-in-out',
        '&:hover': {
            filter: 'brightness(0.97)',
            transform: 'translateX(2px)',
        }
    },
    badgeCounter: {
        backgroundColor: '#F1F5F9', 
        padding: '4px 12px', 
        borderRadius: '20px',
        border: '1px solid #E2E8F0'
    },
    stepPill: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 12px',
        borderRadius: '8px',
        backgroundColor: '#FFFFFF',
        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        marginTop: '6px',
    },
    stepText: {
        fontSize: '0.85rem',
        fontWeight: 600,
        color: '#64748B',
    },
    timeBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        color: '#64748B',
        fontSize: '0.85rem',
        fontWeight: 500,
    }
}));

const AlertasActivas = ({ alertas = [], onAlertClick, loading }) => {
    const { t } = useTranslation();
    const classes = useStyles();

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" style={{ height: '100%' }}>
                <AtCircularProgress />
            </Box>
        );
    }

    return (
            <Box className={classes.scrollList}>
                {alertas.length === 0 ? (
                    <Box 
                        display="flex" 
                        flexDirection="column" 
                        alignItems="center" 
                        justifyContent="center" 
                        style={{ opacity: 0.8, height: '100%', flex: 1 }}
                    >
                        <Box 
                            mb={2} 
                            style={{ 
                                backgroundColor: '#F0FDF4', 
                                borderRadius: '50%', 
                                padding: '16px',
                                display: 'flex'
                            }}
                        >
                            <CancelIcon style={{ color: '#10B981', fontSize: '40px', transform: 'rotate(180deg)' }} />
                        </Box>
                        <Typography variant="body1" style={{ fontWeight: 700, color: '#1E293B' }}>
                            {t('home.events.systemStable', { defaultValue: 'Sistema Estable' })}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#64748B', marginTop: '4px', textAlign: 'center' }}>
                            {t('home.events.noLatestEvents', { defaultValue: 'No se detectan eventos en este momento.' })}
                        </Typography>
                    </Box>
                ) : (
                    alertas.map(alerta => {
                        const config = alertConfig[alerta.tipo] || alertConfig.partial;
                        const { Icon } = config;

                        return (
                            <Box 
                                key={alerta.id} 
                                className={classes.alertItem}
                                onClick={() => onAlertClick?.(alerta.objetivo_id, alerta.monitorNombre)}
                                style={{ 
                                    backgroundColor: config.bg,
                                    borderLeftColor: config.border,
                                }}
                            >
                                <Box style={{ color: config.iconColor, marginTop: '2px' }}>
                                    <Icon style={{ fontSize: '28px' }} />
                                </Box>
                                
                                <Box flex={1}>
                                    <Typography variant="body1" style={{ fontWeight: 800, color: '#0F172A', fontSize: '1.05rem' }}>
                                        {alerta.monitorNombre}
                                    </Typography>
                                    
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                                        <Box className={classes.stepPill}>
                                            <Typography className={classes.stepText}>
                                                {t('home.events.step', { defaultValue: 'Paso:' })} {alerta.pasoNombre}
                                            </Typography>
                                        </Box>
                                        
                                        <Box className={classes.timeBox}>
                                            <AccessTimeIcon style={{ fontSize: '18px' }} />
                                            <span>{alerta.tiempo}</span>
                                        </Box>
                                    </Box>
                                    
                                    <Box mt={1.5}>
                                        <Typography variant="body2" style={{ color: '#475569', fontWeight: 500, lineHeight: 1.4 }}>
                                            {alerta.errors.map(error => error.nombre).join(' | ')}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })
                )}
            </Box>
    );
};

export default AlertasActivas;